"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  fetchLfgLftList,
  fetchLfgLftPost,
  createLfgLftPost,
  updateLfgLftPost,
  lfgLftQueryKeys,
  fetchLfgLftApplicationsIncoming,
  fetchLfgLftApplicationsOutgoing,
  createLfgLftApplication,
  patchLfgLftApplication,
  lfgLftAppQueryKeys,
} from "@/shared/api/lfg-lft-api";
import type {
  LfgLftPostListParams,
  CreateLfgLftPostRequest,
  UpdateLfgLftPostRequest,
  CreateLfgLftApplicationRequest,
  PatchLfgLftApplicationRequest,
} from "@/shared/api/lfg-lft-types";
import { toast } from "sonner";

/** null — параметры ещё не готовы (например, пояс зрителя до гидрации): запрос не уходит. */
export function useLfgLftPosts(params: LfgLftPostListParams | null) {
  return useQuery({
    queryKey: lfgLftQueryKeys.list(params ?? {}),
    queryFn: () => fetchLfgLftList(params ?? {}),
    enabled: params !== null,
  });
}

export function useLfgLftPost(id: string | null) {
  return useQuery({
    queryKey: lfgLftQueryKeys.detail(id ?? ""),
    queryFn: () => (id ? fetchLfgLftPost(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useCreateLfgLftPost() {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLfgLftPostRequest) => createLfgLftPost(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lfgLftQueryKeys.all });
      toast.success(tToast("useLfgLft.obyavlenieSozdano"));
    },
    onError: (err: Error) => {
      toast.error(err.message || tUi("litUseLfgLft.oshibkaSozdaniyaObyavleniya"));
    },
  });
}

export function useUpdateLfgLftPost() {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLfgLftPostRequest }) =>
      updateLfgLftPost(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lfgLftQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: lfgLftQueryKeys.detail(id) });
      toast.success(tToast("useLfgLft.obyavlenieObnovleno"));
    },
    onError: (err: Error) => {
      toast.error(err.message || tUi("litUseLfgLft.oshibkaObnovleniyaObyavleniya"));
    },
  });
}

export function useLfgLftApplicationsIncoming(enabled = true) {
  return useQuery({
    queryKey: lfgLftAppQueryKeys.incoming(),
    queryFn: fetchLfgLftApplicationsIncoming,
    enabled,
  });
}

export function useLfgLftApplicationsOutgoing(enabled = true) {
  return useQuery({
    queryKey: lfgLftAppQueryKeys.outgoing(),
    queryFn: fetchLfgLftApplicationsOutgoing,
    enabled,
  });
}

export function useCreateLfgLftApplication() {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, body }: { postId: string; body: CreateLfgLftApplicationRequest }) =>
      createLfgLftApplication(postId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lfgLftAppQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: lfgLftQueryKeys.all });
      toast.success(tToast("useLfgLft.zayavkaOtpravlena"));
    },
    onError: (err: Error) => {
      toast.error(err.message || tUi("litUseLfgLft.oshibka"));
    },
  });
}

export function usePatchLfgLftApplication() {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchLfgLftApplicationRequest }) =>
      patchLfgLftApplication(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lfgLftAppQueryKeys.all });
      toast.success(tToast("useLfgLft.gotovo"));
    },
    onError: (err: Error) => {
      toast.error(err.message || tUi("litUseLfgLft.oshibka"));
    },
  });
}
